/**
 * MODULE: _shared/frontend/lib/frontend/hooks/useBrainActivityPushSubscription.ts
 * PURPOSE: SSE push for brain.activity.changed — complements poll + same-tab bus [2.2.1257–1258].
 * DEPENDENCIES: brainActivityEvents, brainPushConnection
 * PUBLIC_API: useBrainActivityPushSubscription
 * SIDE_EFFECTS: EventSource connection when tenantId is set; reconnect with backoff on drop
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 95 / 100 lines
 */

import { useEffect } from 'react';
import { notifyBrainActivityChanged } from '../utils/brainActivityEvents';
import { notifyBrainPushStatus } from '../utils/brainPushConnection';

export type UseBrainActivityPushSubscriptionOptions = {
  tenantId?: string | null;
  enabled?: boolean;
  baseUrl?: string;
};

const INITIAL_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;

function parseBrainActivityPayload(raw: string): {
  source?: string;
  scope?: 'all' | 'dashboard' | 'crm';
} {
  try {
    return JSON.parse(raw || '{}') as { source?: string; scope?: 'all' | 'dashboard' | 'crm' };
  } catch {
    return {};
  }
}

function mapPushSource(source?: string): 'postmark' | 'email_sent' | 'sequence_sent' | 'sms_sent' | 'page_engaged' | 'fanout' | 'poll' {
  if (
    source === 'fanout' ||
    source === 'postmark' ||
    source === 'email_sent' ||
    source === 'sequence_sent' ||
    source === 'sms_sent' ||
    source === 'page_engaged'
  ) {
    return source;
  }
  return 'poll';
}

/**
 * Subscribe to brain.activity.changed via GET /api/v1/events/subscribe.
 * On event, emits BRAZN_BRAIN_ACTIVITY_CHANGED so assistant, dashboard, and CRM refresh.
 */
export function useBrainActivityPushSubscription(
  options: UseBrainActivityPushSubscriptionOptions = {},
): void {
  const { tenantId, enabled = true, baseUrl = '' } = options;

  useEffect(() => {
    if (!enabled || !tenantId || typeof window === 'undefined') return;

    let disposed = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: number | undefined;
    let reconnectAttempt = 0;

    const scheduleReconnect = () => {
      if (disposed) return;
      const delay = Math.min(INITIAL_RECONNECT_MS * 2 ** reconnectAttempt, MAX_RECONNECT_MS);
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(connect, delay);
    };

    const connect = () => {
      if (disposed) return;

      eventSource?.close();
      const url =
        `${baseUrl}/api/v1/events/subscribe?tenant_id=${encodeURIComponent(tenantId)}` +
        '&event_type=brain.activity.changed';
      const source = new EventSource(url);
      eventSource = source;

      source.onopen = () => {
        reconnectAttempt = 0;
        notifyBrainPushStatus({ connected: true });
      };

      source.onmessage = (event: MessageEvent) => {
        const data = parseBrainActivityPayload(String(event.data || '{}'));
        notifyBrainActivityChanged({
          source: mapPushSource(data.source),
          scope: data.scope ?? 'all',
        });
      };

      source.onerror = () => {
        source.close();
        if (eventSource === source) {
          eventSource = null;
        }
        notifyBrainPushStatus({ connected: false });
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      disposed = true;
      window.clearTimeout(reconnectTimer);
      eventSource?.close();
      eventSource = null;
      notifyBrainPushStatus({ connected: false });
    };
  }, [tenantId, enabled, baseUrl]);
}
